import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Calendar, momentLocalizer } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import moment from "moment";
import { motion, AnimatePresence } from "framer-motion";
import { useDrag } from "@use-gesture/react";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Sun,
  Moon,
  Calendar as CalendarIcon,
  Clock,
  Trash2,
  Check,
  Edit3,
  Copy,
  Link,
  RefreshCw,
  X,
  Settings,
  Image,
  Type,
  StickyNote,
  Upload,
  Palette,
  Lock,
  Mail,
  Eye,
  EyeOff,
  Pin,
  FileText,
  Search,
  ExternalLink,
  MapPin,
  Video,
  Send,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Switch } from "./components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "./components/ui/context-menu";
import { Toaster, toast } from "sonner";
import "react-big-calendar/lib/css/react-big-calendar.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

const INTEGRATION_TYPES = {
  google: { name: "Google Calendar", icon: "G", color: "#4285F4" },
  koalendar: { name: "Koalendar", icon: "K", color: "#6366F1" },
  outlook: { name: "Outlook", icon: "O", color: "#0078D4" },
  apple: { name: "Apple Calendar", icon: "A", color: "#000000" },
  ical: { name: "iCal URL", icon: "🔗", color: "#64748B" },
};

const WALLPAPERS = [
  { id: "gradient-blue", name: "Ocean", style: "radial-gradient(ellipse at 20% 30%, rgba(0, 122, 255, 0.12) 0%, transparent 50%), radial-gradient(ellipse at 80% 70%, rgba(168, 85, 247, 0.08) 0%, transparent 50%)", type: "gradient" },
  { id: "gradient-purple", name: "Nebula", style: "radial-gradient(ellipse at 30% 20%, rgba(168, 85, 247, 0.15) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(236, 72, 153, 0.1) 0%, transparent 50%)", type: "gradient" },
  { id: "gradient-green", name: "Forest", style: "radial-gradient(ellipse at 20% 80%, rgba(34, 197, 94, 0.12) 0%, transparent 50%), radial-gradient(ellipse at 80% 20%, rgba(20, 184, 166, 0.1) 0%, transparent 50%)", type: "gradient" },
  { id: "gradient-orange", name: "Sunset", style: "radial-gradient(ellipse at 30% 70%, rgba(249, 115, 22, 0.12) 0%, transparent 50%), radial-gradient(ellipse at 70% 30%, rgba(239, 68, 68, 0.08) 0%, transparent 50%)", type: "gradient" },
  { id: "gradient-dark", name: "Midnight", style: "radial-gradient(ellipse at 50% 50%, rgba(30, 30, 40, 0.3) 0%, transparent 70%)", type: "gradient" },
  { id: "none", name: "Clean", style: "none", type: "gradient" },
];

const ACCENT_COLORS = [
  { id: "blue", name: "Blue", color: "#007AFF" },
  { id: "purple", name: "Purple", color: "#A855F7" },
  { id: "pink", name: "Pink", color: "#EC4899" },
  { id: "red", name: "Red", color: "#EF4444" },
  { id: "orange", name: "Orange", color: "#F97316" },
  { id: "green", name: "Green", color: "#22C55E" },
  { id: "teal", name: "Teal", color: "#14B8A6" },
  { id: "indigo", name: "Indigo", color: "#6366F1" },
];

// Login Page Component
const LoginPage = ({ onLogin, accentColor }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      if (response.data.success) {
        localStorage.setItem("authToken", response.data.token);
        onLogin(response.data.token);
        toast.success("Welcome back!");
      }
    } catch (err) {
      setError("Invalid credentials");
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-black">
      {/* Dark gradient background */}
      <div className="fixed inset-0 bg-gradient-to-br from-black via-zinc-900 to-black">
        <div className="absolute inset-0 opacity-30" style={{
          background: `radial-gradient(ellipse at 20% 30%, ${accentColor}15 0%, transparent 50%), 
                       radial-gradient(ellipse at 80% 70%, rgba(100, 100, 100, 0.1) 0%, transparent 50%)`
        }} />
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-sm"
      >
        <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white tracking-tight">Welcome</h1>
            <p className="text-sm text-zinc-500 mt-1">Sign in to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs text-zinc-400">Email</Label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 pl-10 h-11"
                  data-testid="login-email"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-zinc-400">Password</Label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 pl-10 pr-10 h-11"
                  data-testid="login-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-xs text-red-400 text-center">{error}</p>
            )}

            <Button
              type="submit"
              className="w-full h-11 text-white font-medium"
              style={{ background: accentColor }}
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

// Custom Event Component with Glass Effect and Context Menu
const GlassEvent = ({ event, onEdit, onDelete, onDuplicate }) => {
  const categoryColor = event.color || "#3B82F6";
  
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <motion.div
          className="event-pill"
          style={{
            background: `${categoryColor}25`,
            borderLeft: `3px solid ${categoryColor}`,
            boxShadow: `0 1px 4px ${categoryColor}20`,
          }}
          whileHover={{ scale: 1.02 }}
          transition={{ duration: 0.15 }}
        >
          <div className="flex flex-col h-full justify-center px-1">
            <span
              className="font-semibold text-[11px] leading-tight"
              style={{ color: categoryColor }}
            >
              {event.title}
            </span>
            {event.meeting_link && (
              <span 
                className="text-[9px] opacity-70 flex items-center gap-0.5 mt-0.5"
                style={{ color: categoryColor }}
              >
                <Video size={8} /> Meeting
              </span>
            )}
          </div>
        </motion.div>
      </ContextMenuTrigger>
      <ContextMenuContent className="glass-panel border-white/20 min-w-[160px]">
        <ContextMenuItem 
          onClick={() => onEdit(event)}
          className="cursor-pointer"
          data-testid="context-edit"
        >
          <Edit3 size={14} className="mr-2" />
          Edit Event
        </ContextMenuItem>
        <ContextMenuItem 
          onClick={() => onDuplicate(event)}
          className="cursor-pointer"
          data-testid="context-duplicate"
        >
          <Copy size={14} className="mr-2" />
          Duplicate
        </ContextMenuItem>
        {event.meeting_link && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem 
              onClick={() => window.open(event.meeting_link, '_blank')}
              className="cursor-pointer text-blue-500 focus:text-blue-500"
            >
              <ExternalLink size={14} className="mr-2" />
              Join Meeting
            </ContextMenuItem>
          </>
        )}
        <ContextMenuSeparator />
        <ContextMenuItem 
          onClick={() => onDelete(event.id)}
          className="cursor-pointer text-red-500 focus:text-red-500"
          data-testid="context-delete"
        >
          <Trash2 size={14} className="mr-2" />
          Delete
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
};

// Custom Toolbar Component
const CustomToolbar = ({ date, view, onNavigate, onViewChange, isDark, toggleTheme, accentColor }) => {
  const getTitle = () => {
    if (view === "month") {
      return moment(date).format("MMMM YYYY");
    } else if (view === "week") {
      const start = moment(date).startOf("week");
      const end = moment(date).endOf("week");
      if (start.month() === end.month()) {
        return `${start.format("MMMM D")} – ${end.format("D")}`;
      }
      return `${start.format("MMM D")} – ${end.format("MMM D")}`;
    } else {
      return moment(date).format("dddd, MMMM D");
    }
  };

  return (
    <div className="flex items-center justify-between mb-4 px-1">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1">
          <button
            className="nav-arrow"
            onClick={() => onNavigate("PREV")}
            data-testid="nav-prev"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            className="nav-arrow"
            onClick={() => onNavigate("NEXT")}
            data-testid="nav-next"
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <h1 className="text-xl font-bold tracking-tight" data-testid="calendar-title">
          {getTitle()}
        </h1>
        <button
          className="today-btn"
          onClick={() => onNavigate("TODAY")}
          data-testid="nav-today"
          style={{ background: accentColor }}
        >
          Today
        </button>
      </div>

      <div className="flex items-center gap-2">
        <div className="glass-panel rounded-full p-0.5 flex" data-testid="view-toggle">
          {["day", "week", "month"].map((v) => (
            <button
              key={v}
              className={`view-toggle-btn ${view === v ? "active" : ""}`}
              onClick={() => onViewChange(v)}
              data-testid={`view-${v}`}
              style={view === v ? { background: `${accentColor}20`, color: accentColor } : {}}
            >
              {v.charAt(0).toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
        <button
          className="theme-toggle"
          onClick={toggleTheme}
          data-testid="theme-toggle"
        >
          {isDark ? <Sun size={16} /> : <Moon size={16} />}
        </button>
      </div>
    </div>
  );
};

// Event Modal Component
const EventModal = ({ isOpen, onClose, event, onSave, onDelete, isEditing, integrations, categories }) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("personal");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [calendarId, setCalendarId] = useState("local");

  useEffect(() => {
    if (event) {
      setTitle(event.title || "");
      setDescription(event.description || "");
      setCategory(event.category || "personal");
      setAllDay(event.allDay || event.all_day || false);
      setCalendarId(event.integration_id || "local");
      
      const start = moment(event.start);
      const end = moment(event.end);
      
      setStartDate(start.format("YYYY-MM-DD"));
      setStartTime(start.format("HH:mm"));
      setEndDate(end.format("YYYY-MM-DD"));
      setEndTime(end.format("HH:mm"));
    } else {
      setTitle("");
      setDescription("");
      setCategory("personal");
      setStartDate(moment().format("YYYY-MM-DD"));
      setStartTime(moment().format("HH:mm"));
      setEndDate(moment().format("YYYY-MM-DD"));
      setEndTime(moment().add(1, "hour").format("HH:mm"));
      setAllDay(false);
      setCalendarId("local");
    }
  }, [event, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }

    const start = allDay
      ? moment(startDate).startOf("day").toDate()
      : moment(`${startDate} ${startTime}`).toDate();
    const end = allDay
      ? moment(endDate).endOf("day").toDate()
      : moment(`${endDate} ${endTime}`).toDate();

    if (end <= start) {
      toast.error("End time must be after start time");
      return;
    }

    const selectedCal = integrations.find(i => i.id === calendarId);

    onSave({
      ...event,
      title: title.trim(),
      description: description.trim(),
      category,
      start,
      end,
      all_day: allDay,
      color: calendarId !== "local" && selectedCal ? selectedCal.color : categories[category]?.color,
      calendar_id: calendarId,
      calendar_name: calendarId === "local" ? "Local" : selectedCal?.name,
    });
  };

  const calendarOptions = [
    { id: "local", name: "Local Calendar", color: "#3B82F6" },
    ...integrations.filter(i => i.enabled).map(i => ({ id: i.id, name: i.name, color: i.color }))
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-panel border-white/10 max-w-sm" data-testid="event-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">
            {isEditing ? "Edit Event" : "New Event"}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {isEditing ? "Update your time block" : "Create a new time block"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="title" className="text-xs">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What are you working on?"
              className="glass-panel border-white/10 h-9"
              data-testid="event-title-input"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Calendar</Label>
              <Select value={calendarId} onValueChange={setCalendarId}>
                <SelectTrigger className="glass-panel border-white/10 h-9" data-testid="calendar-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass-panel border-white/10">
                  {calendarOptions.map(({ id, name, color }) => (
                    <SelectItem key={id} value={id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ background: color }}
                        />
                        <span className="truncate">{name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="glass-panel border-white/10 h-9" data-testid="category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass-panel border-white/10">
                  {Object.entries(categories).map(([key, { label, color }]) => (
                    <SelectItem key={key} value={key} data-testid={`category-${key}`}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ background: color }}
                        />
                        {label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="allDay"
              checked={allDay}
              onChange={(e) => setAllDay(e.target.checked)}
              className="rounded"
              data-testid="all-day-checkbox"
            />
            <Label htmlFor="allDay" className="cursor-pointer text-xs">
              All day
            </Label>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Start</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="glass-panel border-white/10 h-9 text-xs"
                data-testid="start-date-input"
              />
            </div>
            {!allDay && (
              <div className="space-y-1.5">
                <Label className="text-xs">Time</Label>
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="glass-panel border-white/10 h-9 text-xs"
                  data-testid="start-time-input"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label className="text-xs">End</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="glass-panel border-white/10 h-9 text-xs"
                data-testid="end-date-input"
              />
            </div>
            {!allDay && (
              <div className="space-y-1.5">
                <Label className="text-xs">Time</Label>
                <Input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="glass-panel border-white/10 h-9 text-xs"
                  data-testid="end-time-input"
                />
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description" className="text-xs">Notes</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add notes..."
              className="glass-panel border-white/10 resize-none text-xs"
              rows={2}
              data-testid="event-description-input"
            />
          </div>

          <div className="flex gap-2 pt-1">
            {isEditing && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => onDelete(event.id)}
                className="flex-1"
                data-testid="delete-event-btn"
              >
                <Trash2 size={14} className="mr-1" />
                Delete
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              className="flex-1 glass-panel border-white/10"
              data-testid="cancel-event-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              className="flex-1"
              data-testid="save-event-btn"
            >
              <Check size={14} className="mr-1" />
              {isEditing ? "Save" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Event Preview Modal Component
const EventPreviewModal = ({ isOpen, onClose, event, onEdit, onDelete, onDuplicate, categories, onSendInvite }) => {
  if (!event) return null;
  
  const categoryColor = event.color || categories[event.category]?.color || "#3B82F6";
  const categoryLabel = categories[event.category]?.label || event.category;
  
  const formatTimeRange = () => {
    const start = moment(event.start);
    const end = moment(event.end);
    if (event.all_day || event.allDay) {
      return start.isSame(end, 'day') ? start.format("ddd, MMM D") : `${start.format("MMM D")} - ${end.format("MMM D")}`;
    }
    return `${start.format("ddd, MMM D")} · ${start.format("h:mm A")} - ${end.format("h:mm A")}`;
  };

  // Clean location (remove meeting links from location display)
  const cleanLocation = event.location && !event.location.includes('http') ? event.location : null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-panel border-white/10 max-w-xs p-0 overflow-hidden" data-testid="event-preview-modal">
        {/* Compact Header */}
        <div className="p-3 pb-2" style={{ borderLeft: `3px solid ${categoryColor}` }}>
          <h2 className="text-base font-semibold truncate">{event.title}</h2>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
            <span style={{ color: categoryColor }}>{categoryLabel}</span>
            <span>·</span>
            <span>{formatTimeRange()}</span>
          </div>
        </div>

        {/* Compact Content */}
        <div className="px-3 pb-2 space-y-2">
          {/* Meeting Link - Prominent */}
          {event.meeting_link && (
            <button
              onClick={() => window.open(event.meeting_link, '_blank')}
              className="w-full flex items-center gap-2 p-2 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 transition-colors text-left"
            >
              <Video size={14} className="text-blue-500 shrink-0" />
              <span className="text-xs font-medium text-blue-500 truncate">Join Meeting</span>
              <ExternalLink size={10} className="text-blue-500 ml-auto shrink-0" />
            </button>
          )}

          {/* Location */}
          {cleanLocation && (
            <a 
              href={`https://maps.google.com/?q=${encodeURIComponent(cleanLocation)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 p-2 rounded-lg bg-green-500/10 hover:bg-green-500/20 transition-colors"
            >
              <MapPin size={14} className="text-green-500 shrink-0" />
              <span className="text-xs text-muted-foreground truncate">{cleanLocation}</span>
              <ExternalLink size={10} className="text-green-500 ml-auto shrink-0" />
            </a>
          )}

          {/* Notes - Collapsed */}
          {event.description && (
            <div className="text-xs text-muted-foreground p-2 rounded-lg bg-white/5 max-h-16 overflow-y-auto">
              {event.description}
            </div>
          )}

          {/* Send Invite Button */}
          {!event.synced && (
            <button
              onClick={() => onSendInvite(event)}
              className="w-full flex items-center gap-2 p-2 rounded-lg bg-purple-500/10 hover:bg-purple-500/20 transition-colors text-left"
            >
              <Mail size={14} className="text-purple-500 shrink-0" />
              <span className="text-xs font-medium text-purple-500">Send Meeting Invite</span>
            </button>
          )}
        </div>

        {/* Compact Actions */}
        <div className="p-2 border-t border-white/10 flex gap-1.5">
          <button
            onClick={() => onDuplicate(event)}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs rounded-md hover:bg-white/10 transition-colors text-muted-foreground"
          >
            <Copy size={12} />
          </button>
          <button
            onClick={onEdit}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs rounded-md hover:bg-white/10 transition-colors"
            data-testid="edit-event-btn"
          >
            <Edit3 size={12} />
            Edit
          </button>
          <button
            onClick={() => onDelete(event.id)}
            className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs rounded-md hover:bg-red-500/20 transition-colors text-red-500"
            data-testid="delete-event-btn"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Send Invite Modal Component
const SendInviteModal = ({ isOpen, onClose, event, accentColor }) => {
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setRecipientEmail("");
      setRecipientName("");
      setMessage("");
    }
  }, [isOpen]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!recipientEmail.trim()) {
      toast.error("Please enter recipient email");
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/send-invite`, {
        event_id: event.id,
        recipient_email: recipientEmail.trim(),
        recipient_name: recipientName.trim(),
        message: message.trim(),
      });
      toast.success(response.data.message || "Invite sent successfully!");
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setSending(false);
    }
  };

  if (!event) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-panel border-white/10 max-w-sm" data-testid="send-invite-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold flex items-center gap-2">
            <Send size={18} style={{ color: accentColor }} />
            Send Meeting Invite
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSend} className="space-y-4 mt-2">
          <div className="p-3 rounded-xl bg-white/5 border border-white/10">
            <p className="text-sm font-medium">{event.title}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {moment(event.start).format("ddd, MMM D · h:mm A")} - {moment(event.end).format("h:mm A")}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Recipient Email *</Label>
            <Input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="client@example.com"
              className="glass-panel border-white/10 h-9"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Recipient Name (optional)</Label>
            <Input
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="John Doe"
              className="glass-panel border-white/10 h-9"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Personal Message (optional)</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Looking forward to meeting with you..."
              className="glass-panel border-white/10 min-h-[80px] text-sm resize-none"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              onClick={onClose} 
              className="flex-1 glass-panel border-white/10"
              disabled={sending}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              size="sm" 
              className="flex-1" 
              style={{ background: accentColor }}
              disabled={sending}
            >
              {sending ? (
                <>
                  <RefreshCw size={14} className="mr-1 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={14} className="mr-1" />
                  Send Invite
                </>
              )}
            </Button>
          </div>

          <p className="text-[10px] text-center text-muted-foreground">
            Invite will be sent from No-Reply@aikarotech.com
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Default Meeting Labels
const DEFAULT_MEETING_LABELS = [
  { id: "client", label: "Client Meeting", color: "#A855F7", icon: "👤" },
  { id: "business", label: "Business", color: "#3B82F6", icon: "💼" },
  { id: "consultation", label: "Consultation", color: "#10B981", icon: "💬" },
  { id: "demo", label: "Demo/Presentation", color: "#F59E0B", icon: "📊" },
  { id: "interview", label: "Interview", color: "#EF4444", icon: "🎯" },
  { id: "followup", label: "Follow-up", color: "#6366F1", icon: "🔄" },
  { id: "other", label: "Other", color: "#64748B", icon: "📅" },
];

const REPEAT_OPTIONS = [
  { id: "none", label: "Does not repeat" },
  { id: "daily", label: "Daily" },
  { id: "weekly", label: "Weekly" },
  { id: "biweekly", label: "Every 2 weeks" },
  { id: "monthly", label: "Monthly" },
];

const EMOJI_OPTIONS = ["👤", "💼", "💬", "📊", "🎯", "🔄", "📅", "🏢", "📞", "✈️", "🎉", "📝", "🤝", "💡", "🚀", "⭐"];

// Label Editor Modal
const LabelEditorModal = ({ isOpen, onClose, labels: propsLabels, onSave, onDelete }) => {
  const [labels, setLabels] = useState([]);
  const [editingLabel, setEditingLabel] = useState(null);
  const [newLabel, setNewLabel] = useState({ label: "", color: "#A855F7", icon: "📅" });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load labels when modal opens
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      axios.get(`${API}/meeting-labels`)
        .then(res => setLabels(res.data))
        .catch(() => setLabels(DEFAULT_MEETING_LABELS))
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  const handleSaveLabel = async (labelData, isNew = false) => {
    setSaving(true);
    try {
      if (isNew) {
        const res = await axios.post(`${API}/meeting-labels`, labelData);
        setLabels([...labels, res.data]);
        toast.success("Label created");
        setNewLabel({ label: "", color: "#A855F7", icon: "📅" });
      } else {
        await axios.put(`${API}/meeting-labels/${labelData.id}`, labelData);
        setLabels(labels.map(l => l.id === labelData.id ? labelData : l));
        toast.success("Label updated");
        setEditingLabel(null);
      }
      onSave();
    } catch (error) {
      toast.error("Failed to save label");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLabel = async (id) => {
    if (labels.length <= 1) {
      toast.error("Must have at least one label");
      return;
    }
    try {
      const response = await axios.delete(`${API}/meeting-labels/${id}`);
      console.log('Delete response:', response);
      setLabels(labels.filter(l => l.id !== id));
      toast.success("Label deleted");
      onDelete();
    } catch (error) {
      console.error('Delete error:', error.response || error);
      toast.error(error.response?.data?.detail || error.response?.data?.message || "Failed to delete label");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="border-0 max-w-sm p-0 overflow-hidden"
        style={{
          background: 'rgba(20, 20, 30, 0.9)',
          backdropFilter: 'blur(40px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        }}
      >
        <div className="p-4 border-b border-white/10">
          <h2 className="text-base font-semibold text-white flex items-center gap-2">
            <Settings size={16} />
            Edit Meeting Types
          </h2>
          <p className="text-xs text-white/50 mt-1">Customize labels for your meetings</p>
        </div>

        <div className="p-4 space-y-3 max-h-[50vh] overflow-y-auto">
          {labels.map((l) => (
            <div 
              key={l.id}
              className="flex items-center gap-2 p-2 rounded-xl transition-all"
              style={{ background: editingLabel?.id === l.id ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.03)' }}
            >
              {editingLabel?.id === l.id ? (
                <>
                  <select
                    value={editingLabel.icon}
                    onChange={(e) => setEditingLabel({ ...editingLabel, icon: e.target.value })}
                    className="w-10 h-8 text-center rounded-lg bg-white/10 text-sm border-0 cursor-pointer"
                  >
                    {EMOJI_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                  <input
                    value={editingLabel.label}
                    onChange={(e) => setEditingLabel({ ...editingLabel, label: e.target.value })}
                    className="flex-1 h-8 px-2 rounded-lg bg-white/10 text-sm text-white outline-none"
                  />
                  <input
                    type="color"
                    value={editingLabel.color}
                    onChange={(e) => setEditingLabel({ ...editingLabel, color: e.target.value })}
                    className="w-8 h-8 rounded-lg cursor-pointer border-0"
                  />
                  <button
                    onClick={() => handleSaveLabel(editingLabel)}
                    className="w-8 h-8 rounded-lg bg-green-500/20 text-green-500 flex items-center justify-center hover:bg-green-500/30"
                  >
                    <Check size={14} />
                  </button>
                  <button
                    onClick={() => setEditingLabel(null)}
                    className="w-8 h-8 rounded-lg bg-white/10 text-white/50 flex items-center justify-center hover:bg-white/20"
                  >
                    <X size={14} />
                  </button>
                </>
              ) : (
                <>
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center text-sm" style={{ background: `${l.color}20` }}>
                    {l.icon}
                  </span>
                  <span className="flex-1 text-sm text-white">{l.label}</span>
                  <span className="w-4 h-4 rounded-full" style={{ background: l.color }} />
                  <button
                    onClick={() => setEditingLabel({ ...l })}
                    className="w-7 h-7 rounded-lg text-white/40 flex items-center justify-center hover:bg-white/10 hover:text-white"
                  >
                    <Edit3 size={12} />
                  </button>
                  <button
                    onClick={() => handleDeleteLabel(l.id)}
                    className="w-7 h-7 rounded-lg text-white/40 flex items-center justify-center hover:bg-red-500/20 hover:text-red-500"
                  >
                    <Trash2 size={12} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Add New Label */}
        <div className="p-4 border-t border-white/10">
          <p className="text-[10px] uppercase tracking-wider text-white/40 mb-2">Add New Label</p>
          <div className="flex items-center gap-2">
            <select
              value={newLabel.icon}
              onChange={(e) => setNewLabel({ ...newLabel, icon: e.target.value })}
              className="w-10 h-9 text-center rounded-lg bg-white/10 text-sm border-0 cursor-pointer"
            >
              {EMOJI_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
            <input
              value={newLabel.label}
              onChange={(e) => setNewLabel({ ...newLabel, label: e.target.value })}
              placeholder="Label name..."
              className="flex-1 h-9 px-3 rounded-lg bg-white/10 text-sm text-white placeholder-white/30 outline-none"
            />
            <input
              type="color"
              value={newLabel.color}
              onChange={(e) => setNewLabel({ ...newLabel, color: e.target.value })}
              className="w-9 h-9 rounded-lg cursor-pointer border-0"
            />
            <button
              onClick={() => newLabel.label && handleSaveLabel(newLabel, true)}
              disabled={!newLabel.label || saving}
              className="h-9 px-3 rounded-lg bg-white/10 text-white text-sm flex items-center gap-1 hover:bg-white/20 disabled:opacity-50"
            >
              <Plus size={14} />
            </button>
          </div>
        </div>

        <div className="p-4 pt-0">
          <button
            onClick={onClose}
            className="w-full h-10 rounded-xl text-sm font-medium text-white/60 hover:text-white hover:bg-white/5 transition-all"
          >
            Done
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Quick Client Meeting Modal - Creates event and sends invite in one go
const ClientMeetingModal = ({ isOpen, onClose, accentColor, onEventCreated, meetingLabels, onEditLabels }) => {
  const [title, setTitle] = useState("");
  const [label, setLabel] = useState("client");
  const [date, setDate] = useState(moment().add(1, 'day').format("YYYY-MM-DD"));
  const [startTime, setStartTime] = useState("10:00");
  const [endTime, setEndTime] = useState("11:00");
  const [meetingLink, setMeetingLink] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [message, setMessage] = useState("");
  const [repeat, setRepeat] = useState("none");
  const [creating, setCreating] = useState(false);

  const labels = meetingLabels.length > 0 ? meetingLabels : DEFAULT_MEETING_LABELS;
  const selectedLabel = labels.find(l => l.id === label) || labels[0];

  useEffect(() => {
    if (isOpen) {
      setTitle("");
      setLabel(labels[0]?.id || "client");
      setDate(moment().add(1, 'day').format("YYYY-MM-DD"));
      setStartTime("10:00");
      setEndTime("11:00");
      setMeetingLink("");
      setRecipientEmail("");
      setRecipientName("");
      setMessage("");
      setRepeat("none");
    }
  }, [isOpen, labels]);

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error("Please enter a meeting title");
      return;
    }
    if (!recipientEmail.trim()) {
      toast.error("Please enter client email");
      return;
    }

    setCreating(true);
    try {
      const startDateTime = moment(`${date} ${startTime}`);
      const endDateTime = moment(`${date} ${endTime}`);
      
      // Create events based on repeat setting
      const eventsToCreate = [];
      const repeatCount = repeat === "none" ? 1 : repeat === "daily" ? 7 : repeat === "weekly" ? 4 : repeat === "biweekly" ? 4 : 3;
      
      for (let i = 0; i < repeatCount; i++) {
        let eventStart = startDateTime.clone();
        let eventEnd = endDateTime.clone();
        
        if (repeat === "daily") eventStart.add(i, 'days'), eventEnd.add(i, 'days');
        else if (repeat === "weekly") eventStart.add(i, 'weeks'), eventEnd.add(i, 'weeks');
        else if (repeat === "biweekly") eventStart.add(i * 2, 'weeks'), eventEnd.add(i * 2, 'weeks');
        else if (repeat === "monthly") eventStart.add(i, 'months'), eventEnd.add(i, 'months');
        
        if (i === 0 || repeat !== "none") {
          eventsToCreate.push({
            title: title.trim(),
            description: message.trim() || `${selectedLabel.label} with ${recipientName || recipientEmail}`,
            category: "meeting",
            start: eventStart.toISOString(),
            end: eventEnd.toISOString(),
            all_day: false,
            color: selectedLabel.color,
            meeting_link: meetingLink.trim() || null,
            location: meetingLink.trim() || null,
          });
        }
      }
      
      // Create the first event and send invite
      const eventResponse = await axios.post(`${API}/events`, eventsToCreate[0]);
      const newEventId = eventResponse.data.id;
      
      // Create remaining recurring events
      for (let i = 1; i < eventsToCreate.length; i++) {
        await axios.post(`${API}/events`, eventsToCreate[i]);
      }
      
      // Send the invite for first occurrence
      await axios.post(`${API}/send-invite`, {
        event_id: newEventId,
        recipient_email: recipientEmail.trim(),
        recipient_name: recipientName.trim(),
        message: message.trim(),
      });
      
      const repeatMsg = repeat !== "none" ? ` (${repeatCount} occurrences)` : "";
      toast.success(`Meeting created${repeatMsg} & invite sent!`);
      onEventCreated();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create meeting");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="border-0 max-w-md p-0 overflow-hidden"
        style={{
          background: 'rgba(20, 20, 30, 0.85)',
          backdropFilter: 'blur(40px) saturate(180%)',
          WebkitBackdropFilter: 'blur(40px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
        }}
        data-testid="client-meeting-modal"
      >
        {/* Header */}
        <div 
          className="p-5 pb-4"
          style={{ 
            background: `linear-gradient(135deg, ${selectedLabel.color}15, transparent)`,
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)'
          }}
        >
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-2xl flex items-center justify-center text-lg shadow-lg"
              style={{ 
                background: `linear-gradient(135deg, ${selectedLabel.color}, ${selectedLabel.color}cc)`,
                boxShadow: `0 4px 12px ${selectedLabel.color}40`
              }}
            >
              {selectedLabel.icon}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">New Meeting</h2>
              <p className="text-xs text-white/50">Create & send invite instantly</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleCreate} className="p-5 pt-4 space-y-4">
          {/* Meeting Type Pills */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-[11px] uppercase tracking-wider text-white/40 font-medium">Type</label>
              <button
                type="button"
                onClick={onEditLabels}
                className="text-[10px] text-white/40 hover:text-white/70 flex items-center gap-1 transition-colors"
              >
                <Edit3 size={10} />
                Edit
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {labels.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => setLabel(l.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 flex items-center gap-1.5
                    ${label === l.id 
                      ? 'text-white shadow-lg scale-105' 
                      : 'text-white/60 hover:text-white/80 hover:bg-white/5'
                    }`}
                  style={label === l.id ? { 
                    background: `linear-gradient(135deg, ${l.color}, ${l.color}cc)`,
                    boxShadow: `0 2px 8px ${l.color}40`
                  } : {}}
                >
                  <span>{l.icon}</span>
                  <span>{l.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <label className="text-[11px] uppercase tracking-wider text-white/40 font-medium">Meeting Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={`${selectedLabel.label} with...`}
              className="w-full h-11 px-4 rounded-xl text-sm text-white placeholder-white/30 outline-none transition-all duration-200 focus:ring-2"
              style={{ 
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
              }}
              required
            />
          </div>

          {/* Date & Time Row */}
          <div className="grid grid-cols-5 gap-2">
            <div className="col-span-2 space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-white/40 font-medium">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full h-10 px-3 rounded-xl text-sm text-white outline-none"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  colorScheme: 'dark'
                }}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-white/40 font-medium">Start</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full h-10 px-2 rounded-xl text-sm text-white outline-none"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  colorScheme: 'dark'
                }}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-white/40 font-medium">End</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full h-10 px-2 rounded-xl text-sm text-white outline-none"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  colorScheme: 'dark'
                }}
              />
            </div>
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-white/40 font-medium">Repeat</label>
              <select
                value={repeat}
                onChange={(e) => setRepeat(e.target.value)}
                className="w-full h-10 px-2 rounded-xl text-xs text-white outline-none appearance-none cursor-pointer"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                }}
              >
                {REPEAT_OPTIONS.map(opt => (
                  <option key={opt.id} value={opt.id} className="bg-gray-900">{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Meeting Link */}
          <div className="space-y-2">
            <label className="text-[11px] uppercase tracking-wider text-white/40 font-medium flex items-center gap-1.5">
              <Video size={12} />
              Meeting Link
              <span className="text-white/20">(optional)</span>
            </label>
            <input
              value={meetingLink}
              onChange={(e) => setMeetingLink(e.target.value)}
              placeholder="https://meet.google.com/... or zoom.us/..."
              className="w-full h-10 px-4 rounded-xl text-sm text-white placeholder-white/30 outline-none"
              style={{ 
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
              }}
            />
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] uppercase tracking-wider text-white/30">Invite To</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Client Details */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-white/40 font-medium">Email *</label>
              <input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="client@company.com"
                className="w-full h-10 px-4 rounded-xl text-sm text-white placeholder-white/30 outline-none"
                style={{ 
                  background: 'rgba(168, 85, 247, 0.08)',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                }}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-[11px] uppercase tracking-wider text-white/40 font-medium">Name</label>
              <input
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="John Doe"
                className="w-full h-10 px-4 rounded-xl text-sm text-white placeholder-white/30 outline-none"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                }}
              />
            </div>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <label className="text-[11px] uppercase tracking-wider text-white/40 font-medium">Message <span className="text-white/20">(optional)</span></label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Looking forward to our meeting..."
              rows={2}
              className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-white/30 outline-none resize-none"
              style={{ 
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
              }}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={creating}
              className="flex-1 h-11 rounded-xl text-sm font-medium text-white/60 hover:text-white hover:bg-white/5 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="flex-1 h-11 rounded-xl text-sm font-medium text-white flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.02] disabled:opacity-50"
              style={{ 
                background: `linear-gradient(135deg, ${selectedLabel.color}, ${selectedLabel.color}cc)`,
                boxShadow: `0 4px 12px ${selectedLabel.color}30`
              }}
            >
              {creating ? (
                <>
                  <RefreshCw size={14} className="animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Send size={14} />
                  Create & Send
                </>
              )}
            </button>
          </div>

          <p className="text-[10px] text-center text-white/30">
            Invite sent from No-Reply@aikarotech.com
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Category Modal Component
const CategoryModal = ({ isOpen, onClose, category, onSave, onDelete, isEditing }) => {
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("#3B82F6");

  useEffect(() => {
    if (category && isEditing) {
      setKey(category.key || "");
      setLabel(category.label || "");
      setColor(category.color || "#3B82F6");
    } else {
      setKey("");
      setLabel("");
      setColor("#3B82F6");
    }
  }, [category, isEditing, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!label.trim()) {
      toast.error("Please enter a label");
      return;
    }
    if (!isEditing && !key.trim()) {
      toast.error("Please enter a key");
      return;
    }

    onSave({
      ...category,
      key: isEditing ? category.key : key.trim().toLowerCase().replace(/\s+/g, '_'),
      label: label.trim(),
      color,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-panel border-white/10 max-w-xs" data-testid="category-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">
            {isEditing ? "Edit Category" : "New Category"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          {!isEditing && (
            <div className="space-y-1.5">
              <Label className="text-xs">Key (unique identifier)</Label>
              <Input
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="e.g. exercise"
                className="glass-panel border-white/10 h-9"
                data-testid="category-key-input"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs">Label</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Exercise"
              className="glass-panel border-white/10 h-9"
              data-testid="category-label-input"
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Color</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-10 h-10 rounded cursor-pointer border-0"
                data-testid="category-color-picker"
              />
              <Input
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="glass-panel border-white/10 h-9 flex-1 font-mono text-xs"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            {isEditing && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => onDelete(category.id)}
                data-testid="delete-category-btn"
              >
                <Trash2 size={14} />
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              className="flex-1 glass-panel border-white/10"
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" className="flex-1" data-testid="save-category-btn">
              <Check size={14} className="mr-1" />
              {isEditing ? "Save" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Integration Modal Component
const IntegrationModal = ({ isOpen, onClose, onSave }) => {
  const [type, setType] = useState("ical");
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (isOpen) {
      setType("ical");
      setName("");
      setUrl("");
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const integrationName = name.trim() || INTEGRATION_TYPES[type].name;
    
    if (type === "ical" && !url.trim()) {
      toast.error("Please enter the iCal URL");
      return;
    }

    onSave({
      type,
      name: integrationName,
      url: type === "ical" ? url.trim() : null,
      enabled: true,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-panel border-white/10 max-w-sm" data-testid="integration-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">Add Calendar</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Connect an external calendar
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Calendar Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger className="glass-panel border-white/10 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="glass-panel border-white/10">
                {Object.entries(INTEGRATION_TYPES).map(([key, { name, icon, color }]) => (
                  <SelectItem key={key} value={key}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-5 h-5 rounded flex items-center justify-center text-white text-[10px] font-bold"
                        style={{ background: color }}
                      >
                        {icon}
                      </div>
                      {name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Display Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={INTEGRATION_TYPES[type].name}
              className="glass-panel border-white/10 h-9"
            />
          </div>

          {type === "ical" && (
            <div className="space-y-1.5">
              <Label className="text-xs">iCal URL</Label>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://calendar.google.com/..."
                className="glass-panel border-white/10 h-9 text-xs"
              />
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onClose}
              className="flex-1 glass-panel border-white/10"
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" className="flex-1">
              <Plus size={14} className="mr-1" />
              Add
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Settings Modal Component
const SettingsModal = ({ isOpen, onClose, settings, onSave }) => {
  const [appName, setAppName] = useState(settings.appName || "");
  const [wallpaper, setWallpaper] = useState(settings.wallpaper || "gradient-blue");
  const [customWallpaper, setCustomWallpaper] = useState(settings.customWallpaper || "");
  const [accentColor, setAccentColor] = useState(settings.accentColor || "blue");
  const [customAccentColor, setCustomAccentColor] = useState(settings.customAccentColor || "#007AFF");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setAppName(settings.appName || "");
      setWallpaper(settings.wallpaper || "gradient-blue");
      setCustomWallpaper(settings.customWallpaper || "");
      setAccentColor(settings.accentColor || "blue");
      setCustomAccentColor(settings.customAccentColor || "#007AFF");
    }
  }, [isOpen, settings]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setUploading(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      setCustomWallpaper(event.target.result);
      setWallpaper("custom");
      setUploading(false);
    };
    reader.onerror = () => {
      toast.error('Failed to read image');
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      appName: appName.trim() || "Liquid Time",
      wallpaper,
      customWallpaper: wallpaper === "custom" ? customWallpaper.trim() : settings.customWallpaper,
      accentColor,
      customAccentColor: accentColor === "custom" ? customAccentColor : settings.customAccentColor,
    });
    toast.success("Settings saved");
    onClose();
  };

  const currentAccentColor = accentColor === "custom" 
    ? customAccentColor 
    : ACCENT_COLORS.find(c => c.id === accentColor)?.color || "#007AFF";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="glass-panel border-white/10 max-w-md" data-testid="settings-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">Settings</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label className="text-xs flex items-center gap-1.5">
              <Type size={12} />
              App Name
            </Label>
            <Input
              value={appName}
              onChange={(e) => setAppName(e.target.value)}
              placeholder="Liquid Time"
              className="glass-panel border-white/10 h-9"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1.5">
              <Palette size={12} />
              Theme Color
            </Label>
            <div className="flex flex-wrap gap-2">
              {ACCENT_COLORS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setAccentColor(c.id)}
                  className={`w-7 h-7 rounded-full transition-all flex items-center justify-center
                    ${accentColor === c.id ? 'ring-2 ring-offset-2 ring-offset-background scale-110' : 'hover:scale-105'}`}
                  style={{ background: c.color, ringColor: c.color }}
                >
                  {accentColor === c.id && <Check size={12} className="text-white" />}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setAccentColor("custom")}
                className={`w-7 h-7 rounded-full transition-all flex items-center justify-center border-2 border-dashed
                  ${accentColor === "custom" ? 'ring-2 ring-offset-2 ring-offset-background scale-110' : 'hover:scale-105'}`}
                style={{ 
                  background: accentColor === "custom" ? customAccentColor : "transparent",
                  borderColor: accentColor === "custom" ? customAccentColor : "currentColor"
                }}
              >
                {accentColor !== "custom" && <Plus size={10} />}
                {accentColor === "custom" && <Check size={12} className="text-white" />}
              </button>
            </div>
            {accentColor === "custom" && (
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="color"
                  value={customAccentColor}
                  onChange={(e) => setCustomAccentColor(e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border-0"
                />
                <Input
                  value={customAccentColor}
                  onChange={(e) => setCustomAccentColor(e.target.value)}
                  className="glass-panel border-white/10 h-8 text-xs flex-1 font-mono"
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-xs flex items-center gap-1.5">
              <Image size={12} />
              Wallpaper
            </Label>
            <div className="grid grid-cols-4 gap-2">
              {WALLPAPERS.map((wp) => (
                <button
                  key={wp.id}
                  type="button"
                  onClick={() => setWallpaper(wp.id)}
                  className={`h-10 rounded-lg border-2 transition-all overflow-hidden
                    ${wallpaper === wp.id ? 'scale-105' : 'border-transparent hover:border-white/20'}`}
                  style={{ borderColor: wallpaper === wp.id ? currentAccentColor : undefined }}
                >
                  <div className="w-full h-full" style={{ 
                    background: wp.style === "none" ? "var(--background)" : wp.style,
                    backgroundColor: "var(--background)"
                  }} />
                </button>
              ))}
              <button
                type="button"
                onClick={() => setWallpaper("custom")}
                className={`h-10 rounded-lg border-2 transition-all overflow-hidden flex items-center justify-center
                  ${wallpaper === "custom" ? 'scale-105' : 'border-transparent hover:border-white/20'}`}
                style={{
                  background: customWallpaper ? `url(${customWallpaper}) center/cover` : "var(--background)",
                  borderColor: wallpaper === "custom" ? currentAccentColor : undefined
                }}
              >
                {!customWallpaper && <Upload size={12} className="opacity-50" />}
              </button>
            </div>
          </div>

          {wallpaper === "custom" && (
            <div className="space-y-2">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex-1 glass-panel border-white/10"
                  disabled={uploading}
                >
                  <Upload size={12} className="mr-1.5" />
                  {uploading ? "Uploading..." : "Upload Image"}
                </Button>
              </div>
              <div className="relative">
                <Input
                  value={customWallpaper.startsWith('data:') ? '' : customWallpaper}
                  onChange={(e) => setCustomWallpaper(e.target.value)}
                  placeholder="Or paste image URL..."
                  className="glass-panel border-white/10 h-9 text-xs"
                />
              </div>
              {customWallpaper && (
                <div className="relative h-20 rounded-lg overflow-hidden border border-white/10">
                  <img 
                    src={customWallpaper} 
                    alt="Preview" 
                    className="w-full h-full object-cover"
                    onError={() => toast.error('Failed to load image')}
                  />
                  <button
                    type="button"
                    onClick={() => setCustomWallpaper("")}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/50 flex items-center justify-center hover:bg-black/70"
                  >
                    <X size={10} />
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} className="flex-1 glass-panel border-white/10">
              Cancel
            </Button>
            <Button type="submit" size="sm" className="flex-1" style={{ background: currentAccentColor }}>
              <Check size={14} className="mr-1" />
              Save
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Enhanced Notes Panel Component - Opens Modal
const NotesPanel = ({ accentColor }) => {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingTitle, setEditingTitle] = useState(null);
  const [saveStatus, setSaveStatus] = useState("saved"); // "saved", "saving", "unsaved"
  const saveTimeout = useRef(null);

  const fetchPages = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/note-pages`);
      setPages(response.data);
      if (!selectedPage && response.data.length > 0) {
        setSelectedPage(response.data[0]);
      }
    } catch (error) {
      console.error("Error fetching notes:", error);
    }
  }, [selectedPage]);

  useEffect(() => {
    fetchPages();
  }, []);

  const handleContentChange = (content) => {
    if (!selectedPage) return;
    
    setSelectedPage({ ...selectedPage, content });
    setPages(pages.map(p => p.id === selectedPage.id ? { ...p, content } : p));
    setSaveStatus("unsaved");
    
    if (saveTimeout.current) clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        await axios.put(`${API}/note-pages/${selectedPage.id}`, { content });
        setSaveStatus("saved");
      } catch (error) {
        console.error("Error saving note:", error);
        setSaveStatus("unsaved");
      }
    }, 500);
  };

  const handleTitleChange = async (pageId, newTitle) => {
    setSaveStatus("saving");
    try {
      await axios.put(`${API}/note-pages/${pageId}`, { title: newTitle });
      setPages(pages.map(p => p.id === pageId ? { ...p, title: newTitle } : p));
      if (selectedPage?.id === pageId) {
        setSelectedPage({ ...selectedPage, title: newTitle });
      }
      setEditingTitle(null);
      setSaveStatus("saved");
    } catch (error) {
      console.error("Error updating title:", error);
      setSaveStatus("unsaved");
    }
  };

  const handleCreatePage = async () => {
    try {
      const response = await axios.post(`${API}/note-pages`, { title: "New Note", content: "" });
      setPages([...pages, response.data]);
      setSelectedPage(response.data);
    } catch (error) {
      console.error("Error creating note:", error);
    }
  };

  const handleDeletePage = async (pageId) => {
    if (pages.length <= 1) {
      toast.error("Cannot delete the last note");
      return;
    }
    try {
      await axios.delete(`${API}/note-pages/${pageId}`);
      const remaining = pages.filter(p => p.id !== pageId);
      setPages(remaining);
      if (selectedPage?.id === pageId) {
        setSelectedPage(remaining[0]);
      }
      toast.success("Note deleted");
    } catch (error) {
      console.error("Error deleting note:", error);
    }
  };

  const handleTogglePin = async (page) => {
    try {
      await axios.put(`${API}/note-pages/${page.id}`, { pinned: !page.pinned });
      fetchPages();
    } catch (error) {
      console.error("Error toggling pin:", error);
    }
  };

  const filteredPages = pages.filter(p => 
    p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {/* Compact Trigger Button */}
      <button
        onClick={() => setIsModalOpen(true)}
        className="glass-panel rounded-2xl p-3 w-full flex items-center justify-between hover:bg-white/5 transition-colors"
        data-testid="notes-panel"
      >
        <div className="flex items-center gap-2">
          <StickyNote size={14} />
          <h3 className="font-semibold text-xs">Notes</h3>
          <span className="text-[10px] text-muted-foreground">({pages.length})</span>
        </div>
        <ChevronRight size={14} />
      </button>

      {/* Full Notes Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="glass-panel border-white/10 max-w-3xl h-[80vh] p-0 flex flex-col overflow-hidden" data-testid="notes-modal">
          {/* Header */}
          <div className="p-4 border-b border-white/10 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <StickyNote size={20} style={{ color: accentColor }} />
              <h2 className="text-lg font-semibold">Notes</h2>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search notes..."
                  className="glass-panel border-white/10 h-8 text-sm pl-8 w-48"
                />
              </div>
              <Button
                size="sm"
                className="h-8"
                style={{ background: accentColor }}
                onClick={handleCreatePage}
              >
                <Plus size={14} className="mr-1" />
                New Note
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar - Note List */}
            <div className="w-56 border-r border-white/10 flex flex-col shrink-0">
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {filteredPages.map((page) => (
                  <div
                    key={page.id}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer group transition-colors
                      ${selectedPage?.id === page.id ? 'bg-white/15' : 'hover:bg-white/5'}`}
                    onClick={() => setSelectedPage(page)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        {page.pinned && <Pin size={10} className="text-muted-foreground shrink-0" />}
                        {editingTitle === page.id ? (
                          <input
                            type="text"
                            defaultValue={page.title}
                            className="flex-1 bg-transparent text-sm font-medium outline-none"
                            autoFocus
                            onBlur={(e) => handleTitleChange(page.id, e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleTitleChange(page.id, e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <span 
                            className="text-sm font-medium truncate"
                            onDoubleClick={(e) => { e.stopPropagation(); setEditingTitle(page.id); }}
                          >
                            {page.title}
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                        {page.content?.slice(0, 50) || "Empty note"}
                      </p>
                    </div>
                    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleTogglePin(page); }}
                        className="w-6 h-6 rounded flex items-center justify-center hover:bg-white/10"
                      >
                        <Pin size={12} className={page.pinned ? "fill-current" : ""} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeletePage(page.id); }}
                        className="w-6 h-6 rounded flex items-center justify-center hover:bg-red-500/20 text-red-500"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Editor */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {selectedPage ? (
                <>
                  {/* Note Title */}
                  <div className="p-4 pb-2 shrink-0">
                    <input
                      type="text"
                      value={selectedPage.title}
                      onChange={(e) => {
                        const newTitle = e.target.value;
                        setSelectedPage({ ...selectedPage, title: newTitle });
                        setPages(pages.map(p => p.id === selectedPage.id ? { ...p, title: newTitle } : p));
                      }}
                      onBlur={() => handleTitleChange(selectedPage.id, selectedPage.title)}
                      className="text-xl font-bold bg-transparent outline-none w-full"
                      placeholder="Note title..."
                    />
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-muted-foreground">
                        Last edited {moment(selectedPage.updated_at).fromNow()}
                      </p>
                      <span className="text-xs flex items-center gap-1">
                        {saveStatus === "saving" && (
                          <span className="text-yellow-500 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" />
                            Saving...
                          </span>
                        )}
                        {saveStatus === "saved" && (
                          <span className="text-green-500 flex items-center gap-1">
                            <Check size={12} />
                            Saved
                          </span>
                        )}
                        {saveStatus === "unsaved" && (
                          <span className="text-muted-foreground flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
                            Editing...
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                  {/* Note Content */}
                  <div className="flex-1 p-4 pt-0 overflow-hidden">
                    <textarea
                      value={selectedPage.content}
                      onChange={(e) => handleContentChange(e.target.value)}
                      placeholder="Start writing..."
                      className="w-full h-full bg-transparent resize-none outline-none text-sm leading-relaxed"
                      data-testid="notes-textarea"
                    />
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <StickyNote size={40} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Select a note or create a new one</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Calendar Integrations Section
const IntegrationsSection = ({ integrations, onToggle, onDelete, onAdd, onSync }) => {
  const [syncing, setSyncing] = useState({});

  const handleSync = async (id) => {
    setSyncing(prev => ({ ...prev, [id]: true }));
    await onSync(id);
    setSyncing(prev => ({ ...prev, [id]: false }));
  };

  return (
    <div className="glass-panel rounded-2xl p-3" data-testid="integrations-section">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-xs">Calendars</h3>
        <button onClick={onAdd} className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors">
          <Plus size={12} />
        </button>
      </div>
      
      {integrations.length === 0 ? (
        <p className="text-[10px] text-muted-foreground py-2">No calendars connected</p>
      ) : (
        <div className="space-y-2">
          {integrations.map((integration) => (
            <div key={integration.id} className="flex items-center justify-between group">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div
                  className="w-5 h-5 rounded flex items-center justify-center text-white text-[9px] font-bold flex-shrink-0"
                  style={{ background: integration.color || INTEGRATION_TYPES[integration.type]?.color }}
                >
                  {INTEGRATION_TYPES[integration.type]?.icon || "?"}
                </div>
                <span className={`text-xs truncate ${!integration.enabled ? 'opacity-50' : ''}`}>
                  {integration.name}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {integration.url && (
                  <button
                    onClick={() => handleSync(integration.id)}
                    disabled={syncing[integration.id]}
                    className={`w-5 h-5 rounded flex items-center justify-center hover:bg-white/10 transition-all ${syncing[integration.id] ? 'animate-spin' : 'opacity-0 group-hover:opacity-100'}`}
                  >
                    <RefreshCw size={10} />
                  </button>
                )}
                <button
                  onClick={() => onDelete(integration.id)}
                  className="w-5 h-5 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 hover:bg-red-500/10 text-red-500 transition-all"
                >
                  <X size={10} />
                </button>
                <Switch
                  checked={integration.enabled}
                  onCheckedChange={(checked) => onToggle(integration.id, checked)}
                  className="scale-75"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Categories Section
const CategoriesSection = ({ categories, onEdit, onAdd }) => {
  return (
    <div className="glass-panel rounded-2xl p-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-xs">Categories</h3>
        <button onClick={onAdd} className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors" data-testid="add-category-btn">
          <Plus size={12} />
        </button>
      </div>
      <div className="space-y-1.5">
        {Object.entries(categories).map(([key, { label, color, id }]) => (
          <div
            key={key}
            className="flex items-center gap-2 text-xs group cursor-pointer hover:bg-white/5 rounded p-1 -m-1"
            onClick={() => onEdit({ key, label, color, id })}
          >
            <div className="w-2 h-2 rounded-full" style={{ background: color }} />
            <span className="opacity-80 flex-1">{label}</span>
            <Edit3 size={10} className="opacity-0 group-hover:opacity-50" />
          </div>
        ))}
      </div>
    </div>
  );
};

// Sidebar Mini Calendar
const MiniCalendar = ({ selectedDate, onDateSelect, accentColor }) => {
  const [currentMonth, setCurrentMonth] = useState(moment(selectedDate));

  const daysInMonth = useMemo(() => {
    const start = currentMonth.clone().startOf("month").startOf("week");
    const end = currentMonth.clone().endOf("month").endOf("week");
    const days = [];
    const day = start.clone();
    while (day.isSameOrBefore(end)) {
      days.push(day.clone());
      day.add(1, "day");
    }
    return days;
  }, [currentMonth]);

  const isToday = (day) => day.isSame(moment(), "day");
  const isSelected = (day) => day.isSame(selectedDate, "day");
  const isCurrentMonth = (day) => day.isSame(currentMonth, "month");

  return (
    <div className="glass-panel rounded-2xl p-3" data-testid="mini-calendar">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-xs">{currentMonth.format("MMMM YYYY")}</h3>
        <div className="flex gap-0.5">
          <button className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors" onClick={() => setCurrentMonth(currentMonth.clone().subtract(1, "month"))}>
            <ChevronLeft size={12} />
          </button>
          <button className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-white/10 transition-colors" onClick={() => setCurrentMonth(currentMonth.clone().add(1, "month"))}>
            <ChevronRight size={12} />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center mb-1">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className="text-[9px] font-medium text-muted-foreground py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {daysInMonth.map((day, i) => (
          <button
            key={i}
            onClick={() => onDateSelect(day.toDate())}
            className={`w-6 h-6 rounded-full text-[10px] font-medium transition-all
              ${isCurrentMonth(day) ? "" : "opacity-25"}
              ${isToday(day) ? "text-white" : ""}
              ${isSelected(day) && !isToday(day) ? "bg-white/15" : ""}
              ${!isToday(day) && !isSelected(day) ? "hover:bg-white/10" : ""}`}
            style={isToday(day) ? { background: accentColor } : {}}
          >
            {day.format("D")}
          </button>
        ))}
      </div>
    </div>
  );
};

// Current Time Display
const CurrentTime = ({ accentColor }) => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="glass-panel rounded-2xl p-3" data-testid="current-time">
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: accentColor }}>
          <Clock size={16} className="text-white" />
        </div>
        <div>
          <div className="text-lg font-bold tracking-tight leading-tight">
            {moment(time).format("h:mm")}
            <span className="text-xs font-normal ml-0.5 opacity-70">{moment(time).format("A")}</span>
          </div>
          <div className="text-[10px] text-muted-foreground">{moment(time).format("ddd, MMM D")}</div>
        </div>
      </div>
    </div>
  );
};

// Main Calendar Page
const LiquidTimeCalendar = ({ onLogout }) => {
  const [events, setEvents] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [categories, setCategories] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState("week");
  const [isDark, setIsDark] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [integrationModalOpen, setIntegrationModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCategoryEditing, setIsCategoryEditing] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [sendInviteModalOpen, setSendInviteModalOpen] = useState(false);
  const [inviteEvent, setInviteEvent] = useState(null);
  const [clientMeetingModalOpen, setClientMeetingModalOpen] = useState(false);
  const [labelEditorOpen, setLabelEditorOpen] = useState(false);
  const [meetingLabels, setMeetingLabels] = useState([]);
  const [settings, setSettings] = useState({ appName: "Liquid Time", wallpaper: "gradient-blue", customWallpaper: "", accentColor: "blue", customAccentColor: "#007AFF" });

  useEffect(() => {
    const saved = localStorage.getItem("calendarSettings");
    if (saved) {
      try { setSettings(JSON.parse(saved)); } catch (e) {}
    }
  }, []);

  useEffect(() => {
    const color = settings.accentColor === "custom" ? settings.customAccentColor : ACCENT_COLORS.find(c => c.id === settings.accentColor)?.color || "#007AFF";
    document.documentElement.style.setProperty('--accent-primary', color);
  }, [settings.accentColor, settings.customAccentColor]);

  const accentColor = useMemo(() => {
    return settings.accentColor === "custom" ? settings.customAccentColor : ACCENT_COLORS.find(c => c.id === settings.accentColor)?.color || "#007AFF";
  }, [settings.accentColor, settings.customAccentColor]);

  const wallpaperStyle = useMemo(() => {
    if (settings.wallpaper === "custom" && settings.customWallpaper) {
      return { type: "image", url: settings.customWallpaper };
    }
    const wp = WALLPAPERS.find(w => w.id === settings.wallpaper);
    return { type: "gradient", style: wp?.style || WALLPAPERS[0].style };
  }, [settings.wallpaper, settings.customWallpaper]);

  const toggleTheme = useCallback(() => {
    setIsDark((prev) => {
      document.documentElement.classList.toggle("dark", !prev);
      return !prev;
    });
  }, []);

  const handleSaveSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem("calendarSettings", JSON.stringify(newSettings));
  };

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data.map((e) => ({ ...e, start: new Date(e.start), end: new Date(e.end), allDay: e.all_day })));
    } catch (error) {}
  }, []);

  const fetchIntegrations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/integrations`);
      setIntegrations(response.data);
    } catch (error) {}
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {}
  }, []);

  const fetchMeetingLabels = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/meeting-labels`);
      setMeetingLabels(response.data);
    } catch (error) {}
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchIntegrations();
    fetchCategories();
    fetchMeetingLabels();
  }, [fetchEvents, fetchIntegrations, fetchCategories, fetchMeetingLabels]);

  const handleNavigate = useCallback((action) => {
    setSelectedDate((prev) => {
      const current = moment(prev);
      switch (action) {
        case "PREV": return current.subtract(1, view).toDate();
        case "NEXT": return current.add(1, view).toDate();
        case "TODAY": return new Date();
        default: return prev;
      }
    });
  }, [view]);

  const bind = useDrag(({ direction: [dx], velocity: [vx], last }) => {
    if (last && Math.abs(vx) > 0.3) { dx > 0 ? handleNavigate("PREV") : handleNavigate("NEXT"); }
  }, { axis: "x", filterTaps: true });

  const handleSelectSlot = useCallback(({ start, end }) => {
    setSelectedEvent({ start, end });
    setIsEditing(false);
    setModalOpen(true);
  }, []);

  const handleSelectEvent = useCallback((event) => {
    setSelectedEvent(event);
    setPreviewModalOpen(true);
  }, []);

  const handleEditFromPreview = () => {
    setPreviewModalOpen(false);
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success("Event deleted");
      setModalOpen(false);
      setPreviewModalOpen(false);
      fetchEvents();
    } catch (error) { toast.error("Failed to delete event"); }
  };

  const handleDuplicateEvent = async (event) => {
    try {
      const payload = { title: `${event.title} (copy)`, description: event.description, category: event.category, start: moment(event.start).add(1, 'day').toISOString(), end: moment(event.end).add(1, 'day').toISOString(), all_day: event.all_day || event.allDay, color: event.color };
      await axios.post(`${API}/events`, payload);
      toast.success("Event duplicated");
      setPreviewModalOpen(false);
      fetchEvents();
    } catch (error) { toast.error("Failed to duplicate event"); }
  };

  const handleSendInvite = (event) => {
    setInviteEvent(event);
    setPreviewModalOpen(false);
    setSendInviteModalOpen(true);
  };

  const handleSaveEvent = async (eventData) => {
    try {
      const payload = { title: eventData.title, description: eventData.description, category: eventData.category, start: eventData.start.toISOString(), end: eventData.end.toISOString(), all_day: eventData.all_day, color: eventData.color };
      if (isEditing && eventData.id) {
        await axios.put(`${API}/events/${eventData.id}`, payload);
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/events`, payload);
        toast.success("Event created");
      }
      setModalOpen(false);
      fetchEvents();
    } catch (error) { toast.error("Failed to save event"); }
  };

  const handleEventDrop = async ({ event, start, end }) => {
    if (event.synced) {
      toast.error("Cannot move synced events");
      return;
    }
    try {
      await axios.put(`${API}/events/${event.id}`, {
        ...event,
        start: start.toISOString(),
        end: end.toISOString(),
      });
      fetchEvents();
    } catch (error) { toast.error("Failed to move event"); }
  };

  const handleEventResize = async ({ event, start, end }) => {
    if (event.synced) {
      toast.error("Cannot resize synced events");
      return;
    }
    try {
      await axios.put(`${API}/events/${event.id}`, {
        ...event,
        start: start.toISOString(),
        end: end.toISOString(),
      });
      fetchEvents();
    } catch (error) { toast.error("Failed to resize event"); }
  };

  const handleAddIntegration = async (data) => {
    try {
      const response = await axios.post(`${API}/integrations`, data);
      toast.success(`${data.name} added`);
      setIntegrationModalOpen(false);
      fetchIntegrations();
      if (data.type === "ical" && data.url) {
        toast.loading("Syncing...", { id: "sync" });
        try {
          const syncResponse = await axios.post(`${API}/integrations/${response.data.id}/sync`);
          toast.success(`Synced ${syncResponse.data.count} events`, { id: "sync" });
          fetchEvents();
        } catch { toast.error("Sync failed", { id: "sync" }); }
      }
    } catch (error) { toast.error("Failed to add calendar"); }
  };

  const handleSyncIntegration = async (id) => {
    try {
      const response = await axios.post(`${API}/integrations/${id}/sync`);
      toast.success(`Synced ${response.data.count} events`);
      fetchEvents();
    } catch (error) { toast.error("Sync failed"); }
  };

  const handleToggleIntegration = async (id, enabled) => {
    try {
      await axios.put(`${API}/integrations/${id}`, { enabled });
      fetchIntegrations();
    } catch (error) { toast.error("Failed to update"); }
  };

  const handleDeleteIntegration = async (id) => {
    try {
      await axios.delete(`${API}/integrations/${id}`);
      toast.success("Calendar removed");
      fetchIntegrations();
      fetchEvents();
    } catch (error) { toast.error("Failed to remove"); }
  };

  const handleSaveCategory = async (categoryData) => {
    try {
      if (isCategoryEditing && categoryData.id) {
        await axios.put(`${API}/categories/${categoryData.id}`, { label: categoryData.label, color: categoryData.color });
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/categories`, { key: categoryData.key, label: categoryData.label, color: categoryData.color });
        toast.success("Category created");
      }
      setCategoryModalOpen(false);
      fetchCategories();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to save category"); }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await axios.delete(`${API}/categories/${categoryId}`);
      toast.success("Category deleted");
      setCategoryModalOpen(false);
      fetchCategories();
    } catch (error) { toast.error("Failed to delete category"); }
  };

  const EventWrapper = useCallback(({ event }) => (
    <GlassEvent event={event} onEdit={handleSelectEvent} onDelete={handleDeleteEvent} onDuplicate={handleDuplicateEvent} />
  ), []);

  const calendarComponents = useMemo(() => ({ event: EventWrapper }), [EventWrapper]);

  return (
    <div className={`min-h-screen ${isDark ? "dark" : ""}`} style={{ '--accent-color': accentColor }}>
      <div className="app-background">
        {wallpaperStyle.type === "image" ? (
          <div className="absolute inset-0" style={{ backgroundImage: `url(${wallpaperStyle.url})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(30px) brightness(0.8)", transform: "scale(1.1)", opacity: 0.4 }} />
        ) : (
          <div className="absolute inset-0" style={{ background: wallpaperStyle.style, filter: "blur(60px)", transform: "scale(1.2)" }} />
        )}
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 h-screen p-4 overflow-hidden">
        <aside className="col-span-1 lg:col-span-3 hidden lg:flex flex-col gap-3 overflow-auto scrollbar-hide">
          {/* Frosted glass sidebar container */}
          <motion.div 
            className="glass-sidebar rounded-3xl p-4 flex flex-col gap-4 flex-1"
            initial={{ opacity: 0, x: -20 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ duration: 0.5, ease: "easeOut" }}
            style={{
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(40px) saturate(180%)',
              WebkitBackdropFilter: 'blur(40px) saturate(180%)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.05), 0 20px 40px rgba(0, 0, 0, 0.1)',
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2.5">
                <div 
                  className="w-9 h-9 rounded-xl flex items-center justify-center shadow-lg"
                  style={{ 
                    background: `linear-gradient(135deg, ${accentColor}, ${accentColor}dd)`,
                    boxShadow: `0 4px 12px ${accentColor}40`
                  }}
                >
                  <CalendarIcon size={18} className="text-white" />
                </div>
                <h2 className="text-lg font-semibold tracking-tight">{settings.appName}</h2>
              </div>
              <button 
                onClick={() => setSettingsModalOpen(true)} 
                className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-white/10 transition-all duration-200" 
                data-testid="settings-btn"
              >
                <Settings size={15} className="opacity-70" />
              </button>
            </div>

            {/* Current Time */}
            <CurrentTime accentColor={accentColor} />

            {/* Mini Calendar */}
            <div className="rounded-2xl p-0.5" style={{ background: 'rgba(255, 255, 255, 0.02)' }}>
              <MiniCalendar selectedDate={selectedDate} onDateSelect={(date) => setSelectedDate(date)} accentColor={accentColor} />
            </div>

            {/* Categories */}
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              transition={{ delay: 0.1 }}
              className="rounded-2xl overflow-hidden"
              style={{ background: 'rgba(255, 255, 255, 0.02)' }}
            >
              <CategoriesSection 
                categories={categories} 
                onEdit={(cat) => { setSelectedCategory(cat); setIsCategoryEditing(true); setCategoryModalOpen(true); }}
                onAdd={() => { setSelectedCategory(null); setIsCategoryEditing(false); setCategoryModalOpen(true); }}
              />
            </motion.div>

            {/* Calendars/Integrations */}
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              transition={{ delay: 0.15 }}
              className="rounded-2xl overflow-hidden"
              style={{ background: 'rgba(255, 255, 255, 0.02)' }}
            >
              <IntegrationsSection integrations={integrations} onToggle={handleToggleIntegration} onDelete={handleDeleteIntegration} onAdd={() => setIntegrationModalOpen(true)} onSync={handleSyncIntegration} />
            </motion.div>

            {/* Notes */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
              <NotesPanel accentColor={accentColor} />
            </motion.div>

            {/* Spacer */}
            <div className="flex-1" />

            {/* Actions */}
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              transition={{ delay: 0.25 }} 
              className="space-y-2 pt-2"
            >
              {/* Quick Client Meeting Button */}
              <Button 
                className="w-full rounded-2xl h-10 text-sm font-medium shadow-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-xl" 
                style={{ 
                  background: 'linear-gradient(135deg, #A855F7, #9333EA)',
                  boxShadow: '0 4px 16px rgba(168, 85, 247, 0.3)'
                }} 
                onClick={() => setClientMeetingModalOpen(true)}
                data-testid="client-meeting-btn"
              >
                <Send size={16} className="mr-1.5" />
                Client Meeting
              </Button>
              <Button 
                className="w-full rounded-2xl h-11 text-sm font-medium shadow-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-xl" 
                style={{ 
                  background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc)`,
                  boxShadow: `0 4px 16px ${accentColor}30`
                }} 
                onClick={() => { setSelectedEvent(null); setIsEditing(false); setModalOpen(true); }} 
                data-testid="new-event-btn"
              >
                <Plus size={18} className="mr-1.5" />
                New Event
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full rounded-xl h-9 text-xs opacity-60 hover:opacity-100 hover:bg-white/5 transition-all duration-200" 
                onClick={onLogout}
              >
                Sign Out
              </Button>
            </motion.div>
          </motion.div>
        </aside>

        <motion.main className="col-span-1 lg:col-span-9 flex flex-col relative h-full rounded-2xl overflow-hidden glass-panel p-3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} {...bind()} data-testid="main-calendar">
          <CustomToolbar date={selectedDate} view={view} onNavigate={handleNavigate} onViewChange={setView} isDark={isDark} toggleTheme={toggleTheme} accentColor={accentColor} />
          <div className="flex-1 overflow-auto scrollbar-hide" data-testid="calendar-container">
            <AnimatePresence mode="wait">
              <motion.div key={`${view}-${moment(selectedDate).format("YYYY-MM-DD")}`} initial={{ opacity: 0, x: 15 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -15 }} transition={{ duration: 0.15 }} className="h-full">
                <DnDCalendar localizer={localizer} events={events} startAccessor="start" endAccessor="end" view={view} date={selectedDate} onNavigate={setSelectedDate} onView={setView} selectable onSelectSlot={handleSelectSlot} onSelectEvent={handleSelectEvent} onEventDrop={handleEventDrop} onEventResize={handleEventResize} resizable draggableAccessor={(event) => !event.synced} components={calendarComponents} step={15} timeslots={4} min={new Date(0, 0, 0, 6, 0, 0)} max={new Date(0, 0, 0, 23, 0, 0)} style={{ height: "100%" }} />
              </motion.div>
            </AnimatePresence>
          </div>
          <button className="lg:hidden fixed bottom-4 right-4 w-12 h-12 rounded-full flex items-center justify-center shadow-lg z-50" style={{ background: accentColor }} onClick={() => { setSelectedEvent(null); setIsEditing(false); setModalOpen(true); }} data-testid="mobile-new-event-btn">
            <Plus size={22} className="text-white" />
          </button>
        </motion.main>
      </div>

      <EventModal isOpen={modalOpen} onClose={() => setModalOpen(false)} event={selectedEvent} onSave={handleSaveEvent} onDelete={handleDeleteEvent} isEditing={isEditing} integrations={integrations} categories={categories} />
      <EventPreviewModal isOpen={previewModalOpen} onClose={() => setPreviewModalOpen(false)} event={selectedEvent} onEdit={handleEditFromPreview} onDelete={handleDeleteEvent} onDuplicate={handleDuplicateEvent} onSendInvite={handleSendInvite} categories={categories} />
      <SendInviteModal isOpen={sendInviteModalOpen} onClose={() => setSendInviteModalOpen(false)} event={inviteEvent} accentColor={accentColor} />
      <ClientMeetingModal isOpen={clientMeetingModalOpen} onClose={() => setClientMeetingModalOpen(false)} accentColor={accentColor} onEventCreated={fetchEvents} meetingLabels={meetingLabels} onEditLabels={() => { setClientMeetingModalOpen(false); setLabelEditorOpen(true); }} />
      <LabelEditorModal isOpen={labelEditorOpen} onClose={() => setLabelEditorOpen(false)} labels={meetingLabels} onSave={fetchMeetingLabels} onDelete={fetchMeetingLabels} />
      <CategoryModal isOpen={categoryModalOpen} onClose={() => setCategoryModalOpen(false)} category={selectedCategory} onSave={handleSaveCategory} onDelete={handleDeleteCategory} isEditing={isCategoryEditing} />
      <IntegrationModal isOpen={integrationModalOpen} onClose={() => setIntegrationModalOpen(false)} onSave={handleAddIntegration} />
      <SettingsModal isOpen={settingsModalOpen} onClose={() => setSettingsModalOpen(false)} settings={settings} onSave={handleSaveSettings} />
      <Toaster position="bottom-right" richColors />
    </div>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accentColor] = useState("#007AFF");

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    setIsAuthenticated(!!token);
    setLoading(false);
  }, []);

  const handleLogin = () => setIsAuthenticated(true);
  const handleLogout = () => {
    localStorage.removeItem("authToken");
    setIsAuthenticated(false);
  };

  if (loading) return null;

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <LoginPage onLogin={handleLogin} accentColor={accentColor} />} />
          <Route path="/" element={isAuthenticated ? <LiquidTimeCalendar onLogout={handleLogout} /> : <Navigate to="/login" />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
